import React, { useState, useEffect } from 'react';
import {
    TextField, Button, Box, Typography, Paper, Grid, Card,
    CardMedia, CardContent, CardActions, CircularProgress, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, IconButton,
    Select, MenuItem, FormControl, InputLabel, DialogActions, CardActionArea, Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import CategoryIcon from '@mui/icons-material/Category';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import stateDistricts from '../data/stateDistricts';

function SearchPage() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [categories, setCategories] = useState([]);
    const [query, setQuery] = useState('');
    const [filters, setFilters] = useState({ category: '', state: '', district: '' });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searched, setSearched] = useState(false);

    // Modals
    const [imageModalOpen, setImageModalOpen] = useState(false);
    const [reportModalOpen, setReportModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [reportReason, setReportReason] = useState('');

    // Toast
    const [toast, setToast] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Search function
    const performSearch = async (currentQuery, currentFilters) => {
        if (!currentQuery && !currentFilters.category && !currentFilters.state && !currentFilters.district) {
            setResults([]);
            setSearched(true);
            return;
        }

        setLoading(true);
        setError('');
        setSearched(true);
        try {
            const params = new URLSearchParams();
            if (currentQuery) params.append('q', currentQuery);
            if (currentFilters.category) params.append('category', currentFilters.category);
            if (currentFilters.state) params.append('state', currentFilters.state);
            if (currentFilters.district) params.append('district', currentFilters.district);

            const { data } = await axios.get(`/api/items/search?${params.toString()}`);
            setResults(data);
        } catch (err) {
            setError('Failed to fetch search results.');
        } finally {
            setLoading(false);
        }
    };

    // Fetch categories + initial search
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/categories');
                setCategories(data);
            } catch (error) {
                console.error("Could not fetch categories");
            }
        };
        fetchCategories();
        performSearch(searchParams.get('q') || '', filters);
    }, [searchParams]);

    // Filter change
    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => {
            const newFilters = { ...prev, [name]: value };
            if (name === 'state') newFilters.district = '';
            return newFilters;
        });
    };

    // Search submit
    const handleSearch = (e) => {
        e.preventDefault();
        setSearchParams({ q: query });
        performSearch(query, filters);
    };

    // Image click
    const handleImageClick = (item) => {
        setSelectedItem(item);
        setImageModalOpen(true);
    };

    // Report click
    const handleReportClick = (item) => {
        if (!token) {
            alert("Please login to report an item.");
            navigate('/login');
            return;
        }
        setSelectedItem(item);
        setReportModalOpen(true);
    };

    const handleClose = () => {
        setImageModalOpen(false);
        setReportModalOpen(false);
        setSelectedItem(null);
        setReportReason('');
        setError('');
    };

    // Submit report
    const submitReport = async () => {
        if (!reportReason.trim()) {
            setError("Please provide a reason for reporting.");
            return;
        }
        setError('');
        try {
            const { data } = await axios.post(`/api/items/${selectedItem._id}/report`,
                { reason: reportReason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setToast(data.message);
            setSnackbarOpen(true);
            handleClose();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to submit report.");
        }
    };

    // Start chat
    const handleStartChat = async (item) => {
        if (!token) {
            alert("Please login to contact the finder.");
            navigate('/login');
            return;
        }
        try {
            const { data: conversation } = await axios.post('/api/messaging/start',
                { itemId: item._id, recipientId: item.foundBy },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            navigate(`/chat/${conversation._id}`);
        } catch (err) {
            setError(err.response?.data?.message || "Could not start conversation.");
        }
    };

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Search for Found Items</Typography>
            <form onSubmit={handleSearch}>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Search by item name, description..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select name="category" value={filters.category} label="Category" onChange={handleFilterChange}>
                                <MenuItem value=""><em>Any</em></MenuItem>
                                {categories.map(cat => <MenuItem key={cat._id} value={cat.name}>{cat.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>State</InputLabel>
                            <Select name="state" value={filters.state} label="State" onChange={handleFilterChange}>
                                <MenuItem value=""><em>Any</em></MenuItem>
                                {Object.keys(stateDistricts).map(state => <MenuItem key={state} value={state}>{state}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth disabled={!filters.state}>
                            <InputLabel>District</InputLabel>
                            <Select name="district" value={filters.district} label="District" onChange={handleFilterChange}>
                                <MenuItem value=""><em>Any</em></MenuItem>
                                {filters.state && stateDistricts[filters.state].map(district => <MenuItem key={district} value={district}>{district}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Button type="submit" variant="contained" disabled={loading} fullWidth>
                    Search
                </Button>
            </form>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>}
            {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

            <Box mt={4}>
                {searched && !loading && results.length === 0 && (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6">No items found for your search criteria.</Typography>
                    </Paper>
                )}
                <Grid container spacing={3}>
                    {results.map(item => (
                        <Grid item xs={12} sm={6} md={4} key={item._id}>
                            <Card className="search-result-card">
                                <CardActionArea onClick={() => navigate(`/item/${item._id}`)}>
                                    <CardMedia
                                        component="img"
                                        height="160"
                                        image={item.image || 'https://via.placeholder.com/300?text=No+Image'}
                                        alt={item.itemName}
                                    />
                                    <CardContent sx={{ flexGrow: 1 }}>
                                        <Typography gutterBottom variant="h6" component="div" noWrap>
                                            {item.itemName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ height: 40, overflow: 'hidden' }}>
                                            {item.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, color: 'text.secondary' }}>
                                            <CategoryIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography variant="caption">{item.category}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, color: 'text.secondary' }}>
                                            <LocationOnIcon fontSize="small" sx={{ mr: 0.5 }} />
                                            <Typography variant="caption">{item.district}, {item.state}</Typography>
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                                <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
                                    <Button size="small" variant="contained" onClick={() => handleStartChat(item)}>Contact</Button>
                                    <IconButton title="Report this item" onClick={() => handleReportClick(item)}>
                                        <FlagIcon color="action" />
                                    </IconButton>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Image Modal */}
            <Dialog open={imageModalOpen} onClose={handleClose} maxWidth="md">
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}>
                    <CloseIcon />
                </IconButton>
                <img src={selectedItem?.image} alt={selectedItem?.itemName} style={{ width: '100%', height: 'auto' }} />
            </Dialog>

            {/* Report Modal */}
            <Dialog open={reportModalOpen} onClose={handleClose}>
                <DialogTitle>Report Item: {selectedItem?.itemName}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Please provide a reason for reporting this item (e.g., spam, inappropriate content, false information).
                    </DialogContentText>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Reason for reporting"
                        type="text"
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        value={reportReason}
                        onChange={(e) => setReportReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button onClick={submitReport} variant="contained" color="error">Submit Report</Button>
                </DialogActions>
            </Dialog>

            {/* Toast Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={4000}
                onClose={() => setSnackbarOpen(false)}
                message={toast}
            />
        </Paper>
    );
}

export default SearchPage;
